// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Tables Screen
// Floor plan view (or grid fallback) + table info card + actions
// ---------------------------------------------------------------------------

import { useState, useMemo, useCallback } from 'react';
import FloorPlanView from '@/components/FloorPlanView';
import TableGrid from '@/components/TableGrid';
import TableInfoCard from '@/components/TableInfoCard';
import { useTableStore } from '@/store/table-store';
import { useCartStore } from '@/store/cart-store';
import { useAppStore } from '@/store/app-store';
import { apiClient } from '@/services/api-client';
import { useTables } from '@/hooks/useTables';
import type { Table } from '@/lib/types';

export default function TablesScreen() {
  useTables();

  const floorPlans = useTableStore((s) => s.floorPlans);
  const tables = useTableStore((s) => s.tables);
  const selectedTableId = useTableStore((s) => s.selectedTableId);
  const selectTable = useTableStore((s) => s.selectTable);
  const showToast = useAppStore((s) => s.showToast);
  const setScreen = useAppStore((s) => s.setScreen);
  const setTable = useCartStore((s) => s.setTable);

  const [viewMode, setViewMode] = useState<'plan' | 'grid'>(
    floorPlans.length > 0 ? 'plan' : 'grid',
  );
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState('');

  const selectedTable = useMemo(() => {
    if (!selectedTableId) return null;
    return tables[selectedTableId] ?? null;
  }, [tables, selectedTableId]);

  const tablesList = useMemo(() => Object.values(tables), [tables]);

  const handleTableClick = useCallback(
    (id: string) => {
      selectTable(selectedTableId === id ? null : id);
    },
    [selectTable, selectedTableId],
  );

  // ---- Actions ----

  const handleLock = async () => {
    if (!selectedTableId) return;
    try {
      await apiClient.lockTable(selectedTableId);
      showToast('Table verrouillee', 'success');
    } catch {
      showToast('Erreur verrouillage table', 'error');
    }
  };

  const handleRelease = async () => {
    if (!selectedTableId) return;
    try {
      await apiClient.releaseTable(selectedTableId);
      showToast('Table liberee', 'success');
    } catch {
      showToast('Erreur liberation table', 'error');
    }
  };

  const handleInstantReservation = async () => {
    if (!selectedTableId) return;
    try {
      await apiClient.createInstantReservation(selectedTableId, {});
      showToast('Resa minute creee', 'success');
    } catch {
      showToast('Erreur creation resa minute', 'error');
    }
  };

  const handleTransfer = () => {
    if (!selectedTableId) return;
    setShowTransferDialog(true);
    setTransferTargetId('');
  };

  const handleTransferConfirm = async () => {
    if (!selectedTableId || !transferTargetId) return;
    const reservation = selectedTable?.reservation_id;
    if (!reservation) {
      // No reservation to transfer — just associate table to cart
      setTable(selectedTableId);
      setScreen('caisse');
      showToast(`Table ${selectedTable?.name ?? selectedTableId} associee au panier`, 'info');
      setShowTransferDialog(false);
      return;
    }
    try {
      await apiClient.transferReservation(reservation, transferTargetId);
      showToast(`Reservation transferee vers table ${transferTargetId}`, 'success');
      setShowTransferDialog(false);
    } catch {
      showToast('Erreur transfert reservation', 'error');
    }
  };

  const handleAssociateToCart = () => {
    if (!selectedTableId) return;
    setTable(selectedTableId);
    setScreen('caisse');
    showToast(`Table ${selectedTable?.name ?? selectedTableId} associee au panier`, 'info');
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header with view toggle */}
      <div className="flex items-center justify-between px-4 py-2 shrink-0">
        <h2 className="text-base font-semibold text-white">Tables</h2>

        {floorPlans.length > 0 && (
          <div className="flex bg-surface-card rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setViewMode('plan')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'plan'
                  ? 'bg-accent text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Plan
            </button>
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'grid'
                  ? 'bg-accent text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Grille
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'plan' && floorPlans.length > 0 ? (
          <FloorPlanView
            floorPlans={floorPlans}
            tables={tables}
            onTableClick={handleTableClick}
          />
        ) : (
          <div className="h-full overflow-y-auto">
            <TableGrid
              tables={tablesList}
              onTableClick={handleTableClick}
              selectedId={selectedTableId}
            />
          </div>
        )}
      </div>

      {/* Selected table info card */}
      {selectedTable && (
        <TableInfoCard
          table={selectedTable}
          onLock={handleLock}
          onRelease={handleRelease}
          onInstantReservation={handleInstantReservation}
          onTransfer={handleTransfer}
          onAssociateToCart={handleAssociateToCart}
        />
      )}

      {/* Transfer dialog */}
      {showTransferDialog && (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/60">
          <div className="bg-surface-card rounded-2xl p-6 w-80 animate-fade-in">
            <h3 className="text-base font-semibold text-white mb-3">Transferer la reservation</h3>
            <p className="text-sm text-gray-400 mb-4">Choisissez la table de destination</p>
            <select
              value={transferTargetId}
              onChange={(e) => setTransferTargetId(e.target.value)}
              className="w-full bg-surface text-white rounded-lg px-3 py-2 mb-4 border border-white/10 focus:border-accent outline-none"
            >
              <option value="">-- Selectionnez --</option>
              {tablesList
                .filter((t) => t.id !== selectedTableId && t.state === 'libre')
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name || `Table ${t.id}`}
                  </option>
                ))}
            </select>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowTransferDialog(false)}
                className="btn-secondary flex-1"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleTransferConfirm}
                disabled={!transferTargetId}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                Transferer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
